import Plot, { LocalisedPlotProps } from "components/plotly/LocalisedPlot";
import type { Data, Layout, PlotMouseEvent } from "plotly.js";
import { useCallback, useMemo, useState } from "react";
import {
  useResolvedScrapViewportTimeZone,
  useViewportRelayoutHandler,
} from "store/slices/scrapTrackingViewportSlice";
import {
  formatPlotlyTime,
  getPlotlyDateTicks,
  plotlyUtcDate,
  plotlyUtcMilliseconds,
  preparePlotlyDateTrace,
} from "src/utils/plotlyDateAxis";

type ScrapTrackingViewportPlotProps = Omit<LocalisedPlotProps, "onRelayout">;

export default function ScrapTrackingViewportPlot({
  data,
  layout,
  onHover,
  ...rest
}: ScrapTrackingViewportPlotProps) {
  const timeZone = useResolvedScrapViewportTimeZone();
  const handleRelayout = useViewportRelayoutHandler();
  const [hoveredTimestamp, setHoveredTimestamp] = useState<number | null>(null);
  const handleHover = useCallback(
    (event: PlotMouseEvent) => {
      if (layout.hovermode === "x unified") {
        const timestamp = plotlyUtcMilliseconds(event.points[0]?.x);
        setHoveredTimestamp(Number.isFinite(timestamp) ? timestamp : null);
      }
      onHover?.(event);
    },
    [layout.hovermode, onHover]
  );
  const preparedData = useMemo(
    () =>
      (data as Data[] | undefined)?.map((trace) =>
        preparePlotlyDateTrace({ trace, timeZone, hovermode: layout.hovermode })
      ),
    [data, timeZone, layout.hovermode]
  );

  const preparedLayout = useMemo<Partial<Layout>>(() => {
    const [start, end] =
      (layout.xaxis?.range as readonly unknown[] | undefined) ?? [];
    if (!(start instanceof Date) || !(end instanceof Date)) return layout;

    return {
      ...layout,
      xaxis: {
        ...layout.xaxis,
        range: [plotlyUtcDate(start), plotlyUtcDate(end)],
        tickmode: "array",
        ...getPlotlyDateTicks({ start, end, timeZone }),
      },
      shapes: layout.shapes?.map((shape) =>
        shape.xref === "x" || !shape.xref
          ? {
              ...shape,
              x0: plotlyUtcDate(shape.x0),
              x1: plotlyUtcDate(shape.x1),
            }
          : shape
      ),
    };
  }, [layout, timeZone]);

  const layoutWithHoverTitle = {
    ...preparedLayout,
    xaxis: {
      ...preparedLayout.xaxis,
      unifiedhovertitle: {
        // An empty string restores the UTC heading.
        text:
          hoveredTimestamp === null
            ? " "
            : formatPlotlyTime({
                date: new Date(hoveredTimestamp),
                timeZone,
              }),
      },
    },
  };

  return (
    <Plot
      {...rest}
      data={preparedData}
      layout={layoutWithHoverTitle}
      onRelayout={handleRelayout}
      onHover={handleHover}
    />
  );
}
